import { CommonModule, UpperCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, computed, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_LUXON_DATE_FORMATS, MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatPaginator } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Attendee, MY_DATE_FORMATS, Registration } from '../../../domain/registrator';
import { RegistrationService } from '../../../services/registration.service';
import { ConfirmDialogComponent } from '../../misc/confirm-dialog/confirm-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface AttendeeWithRegistration extends Attendee {
  organisation: string;
  registrationSeqNum: number;
  registrationDate: string;
  registrationId: number;
}


@Component({
  selector: 'app-event-free-registrations',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatRadioModule,
    MatTableModule,
    MatCardModule,
    MatListModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatPaginator,
    MatTabsModule,
    MatDatepickerModule,
    MatLuxonDateModule,
    MatSortModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
  ],
  templateUrl: './event-free-registrations.component.html',
  styleUrl: './event-free-registrations.component.scss',
})
export class EventFreeRegistrationsComponent implements OnInit, AfterViewInit {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly registrationService = inject(RegistrationService);

  private readonly route = inject(ActivatedRoute);
  event = this.route.parent!.snapshot.data['event'];

  loading = signal<boolean>(false);
  registrationsListItems = signal<Registration[]>([]);
  allAttendees = computed<AttendeeWithRegistration[]>(() => {
    return this.registrationsListItems().flatMap(reg =>
      (reg.attendees ?? []).map(att => ({
        ...att,
        organisation: reg.organisation,
        registrationId: reg.id,
        registrationSeqNum: reg.seqNum,
        registrationDate: reg.date,
      }))
    );
  });

  attendeesDataSource: MatTableDataSource<AttendeeWithRegistration> = new MatTableDataSource();
  filterForm: FormGroup;
  pageSize: number = 10;
  pageIndex: number = 0;
  showFirstLastButtons: boolean = true;
  pageSizeOptions = [5, 10, 25, 100];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  columnsToDisplay = ['registrationSeqNum', 'registrationDate', 'firstName', 'lastName',
    'email', 'jobTitle', 'organisation', 'confirmationSentOn', 'actions'];

  constructor() {
    this.filterForm = this.formBuilder.group({
      name: this.formBuilder.control<string>(''),
      organisation: this.formBuilder.control(['',]),
    });

    effect(() => {
      this.attendeesDataSource.data = this.allAttendees();
    });
  }

  ngOnInit() {
    this.loadRegistrations();
  }

  ngAfterViewInit() {
    this.attendeesDataSource.paginator = this.paginator;
    this.attendeesDataSource.sort = this.sort;
    this.sort!.active = 'seqNum';  // Default sorting column
    this.sort!.direction = 'asc'; // Default order (ascending)        
    this.cdr.detectChanges(); // Trigger change detection after setting up the data source
  }

  loadRegistrations() {
    this.loading.set(true);
    this.registrationService.getEventRegistrations(this.event.shortName, undefined).subscribe({
      next: (list) => {
        this.loading.set(false);
        this.registrationsListItems.set(list);
        this.attendeesDataSource.data = this.allAttendees();

        let s = sessionStorage.getItem('attendeeListPageSize');
        if (s) {
          let page = parseInt(s);
          if (!isNaN(page)) {
            if (this.pageSizeOptions.includes(page)) {
              // console.log(`setting pagesize to ${s}`);
              this.pageSize = page;
            }
          }
        }
        s = sessionStorage.getItem('attendeeListCurrentPage');
        if (s) {
          let page = parseInt(s);
          if (!isNaN(page)) {
            if ((page > 0) && (page < Math.ceil(this.allAttendees().length / this.pageSize))) {
              // console.log(`jumping to page ${s}`);
              this.pageIndex = page;
            }
          }
        }
      },
      error: (error) => {
        console.log(error);
        this.loading.set(false);
        if (error?.error?.message) {
          console.error('Error message:', error.error.message);
          this.snackBar.open('Error loading registrations' + error.error.message.toLowerCase(), 'Dismiss', { duration: 3000 });
        } else {
          this.snackBar.open('Error loading registrations', 'Dismiss', { duration: 3000 });
        }
      }
    });
  }

  /**
   * 
   * @param r 
   */

  updateAttendeeConfirmation(registrationId: number, attendeeId: number, confirmationDate: string) {
    console.log('updateAttendeeConfirmation', registrationId, attendeeId, confirmationDate)
    this.registrationsListItems.update(regs =>
      regs.map(reg =>
        reg.id === registrationId
          ? {
            ...reg,
            attendees: reg.attendees.map(att =>
              att.id === attendeeId
                ? { ...att, confirmationSentOn: confirmationDate, firstName: 'blabla' }
                : att
            )
          }
          : reg
      )
    );
  }

  removeAttendee(registrationId: number, attendeeId: number) {
    console.log('removeAttendee', registrationId, attendeeId)
    this.registrationsListItems.update(regs =>
      regs.map(reg =>
        reg.id === registrationId
          ? {
            ...reg,
            attendees: reg.attendees.filter(att => att.id !== attendeeId)
          }
          : reg
      )
    );
  }

  sendConfirmation(r: AttendeeWithRegistration) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      message: (!r.confirmationSentOn ?
        `Are you sure you want to confirm ${r.firstName} ${r.lastName.toUpperCase()}'s attendance and send email?` :
        `Are you sure you want to resend the attendance's confirmation email to ${r.firstName} ${r.lastName.toUpperCase()}?`),
      buttonText: {
        ok: 'Yes',
        cancel: 'Cancel'
      }
    };
    dialogConfig.height = '200px';
    dialogConfig.width = '600px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.loading.set(true);
        this.registrationService.confirmAttendee(r.registrationId, r.id!).subscribe({
          next: (result) => {
            const msg = (!r.confirmationSentOn ? 'Registration confirmed' : 'Confirmation re-sent');
            if (!r.confirmationSentOn) {
              this.updateAttendeeConfirmation(result.registrationId, result.attendeeId, result.confirmationDate);
            }
            this.snackBar.open(msg, 'Dismiss', { duration: 3000 });
          },
          error: (error) => {
            console.error(error);
            this.loading.set(false);
            if (error?.error?.message) {
              this.snackBar.open('Error confirming registration' + error.error.message.toLowerCase(), 'Dismiss', { duration: 3000 });
            } else {
              this.snackBar.open('Error confirming registration', 'Dismiss', { duration: 3000 });
            }
          }
        });
        this.loading.set(false);
      }
    })
  }

  deleteAttendee(r: AttendeeWithRegistration) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      message: (!r.confirmationSentOn ?
        `Are you sure you want to delete ${r.firstName} ${r.lastName.toUpperCase()}'s attendance. This can't be undone.` :
        `Are you sure you want to delete ${r.firstName} ${r.lastName.toUpperCase()}'s attendance. It has already been confirmed via email. This can't be undone.`),
      buttonText: {
        ok: 'Yes',
        cancel: 'Cancel'
      }
    };
    dialogConfig.height = '200px';
    dialogConfig.width = '600px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.loading.set(true);
        this.registrationService.deleteAttendee(r.registrationId, r.id!).subscribe({
          next: (result) => {
            this.removeAttendee(r.registrationId, r.id!);
            this.snackBar.open("Attendee has been deleted.", 'Dismiss', { duration: 3000 });
          },
          error: (error) => {
            console.error(error);
            this.loading.set(false);
            if (error?.error?.message) {
              this.snackBar.open('Error deleting registration: ' + error.error.message.toLowerCase(), 'Dismiss', { duration: 3000 });
            } else {
              this.snackBar.open('Error deleting registration.', 'Dismiss', { duration: 3000 });
            }
          }
        });
        this.loading.set(false);
      }
    })
  }
}
